<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaDatePicker from '@/components/design/base/inputs/GaiaDatePicker.vue';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaAvatar from '@/components/design/base/GaiaAvatar.vue';
import GaiaSegmented, {
  type GaiaSegmentedOption,
} from '@/components/design/base/GaiaSegmented.vue';
import {
  IconArrowDown,
  IconArrowUp,
  IconDocumentText,
  IconFolder,
  IconReorder,
} from '@/components/design/icons';
import { initialsOf } from '@/utils/initials';
import type { SignerLine } from '@/services/contractGeneration/entityFieldService';
import { signatureTypeMap } from '@/components/design/statusMaps';
import { gaiaNeutral } from '@/components/design/themes/tokens';
import { SignatureType, type ContractGenerationFinalizeSigning, type SigningProvider } from '@/api';
import { useSigningProviderOptions } from '@/composables/useSigningProviderOptions';
import {
  expiryDayInDays,
  expiryDayToIso,
  isUnavailableExpiryDay,
} from '@/composables/useSigningWizard';

/**
 * "Save & sign": the document's name, the folder it was created in (moved
 * from the vault, not here), and how it is sent — provider, signature type, order, message, expiry. The
 * wizard owns the saving, and afterwards takes the author to the signing
 * request; this owns the asking.
 */
interface Props {
  show: boolean;
  fileName: string;
  /** The folder as a path, e.g. "My Vault / Agreements". */
  folderLabel: string | null;
  saving: boolean;
  /**
   * What still stops sending for signing, one line per signing slot; empty
   * when ready. Null when the template places no signatures, so it can't be sent.
   */
  signerGaps: string[] | null;
  /** Who signs, in the template's order, one line each; reorderable here. */
  signers: SignerLine[];
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:show': [value: boolean];
  save: [
    payload: {
      fileName: string;
      signing: ContractGenerationFinalizeSigning;
    },
  ];
}>();

const { providerOptions, defaultProvider, expirationDays } = useSigningProviderOptions();
const provider = ref<SigningProvider | null>(null);
/** Only a provider the author picked here is sent; otherwise the backend uses the org's default. */
const providerOverridden = ref<boolean>(false);
// The backend's one-click defaults, until changed here.
const signatureType = ref<SignatureType>(SignatureType.ELECTRONIC_SIGNATURE);
const orderedSigners = ref<boolean>(false);
const signingMessage = ref<string>('');
const expiresAt = ref<number | null>(null);
/**
 * The signing slots in the order they sign. Only the order is kept here: the
 * lines are read live from `signers`, whose names fill in as parties load.
 */
const slotOrder = ref<string[]>([]);
const order = computed<SignerLine[]>(() =>
  slotOrder.value.flatMap(slot => props.signers.find(line => line.slot === slot) ?? [])
);

/** The signing wizard's switch, worded the same. */
const ORDER_OPTIONS: GaiaSegmentedOption[] = [
  { label: 'All at once', value: 'parallel' },
  { label: 'In order', value: 'sequential' },
];

/** "Acme GmbH: Jane Doe" reads as Jane Doe, signing for Acme GmbH. */
function splitSigner(label: string): { person: string; company: string | null } {
  const at = label.indexOf(': ');
  return at === -1
    ? { person: label, company: null }
    : { person: label.slice(at + 2), company: label.slice(0, at) };
}

function move(from: number, to: number): void {
  const next = order.value.map(line => line.slot);
  const [slot] = next.splice(from, 1);
  next.splice(to, 0, slot);
  slotOrder.value = next;
}

// Drag to reorder, the same native drag-and-drop as the signing wizard's recipients.
const dragFrom = ref<number | null>(null);
const dragOver = ref<number | null>(null);

function onDragStart(index: number, event: DragEvent): void {
  if (props.saving || !orderedSigners.value) return;
  event.dataTransfer?.setData('text/plain', String(index));
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';
  // Chromium cancels the drag if the dragged element re-renders inside dragstart.
  setTimeout(() => (dragFrom.value = index), 0);
}

function onDragOver(index: number, event: DragEvent): void {
  if (dragFrom.value === null) return;
  event.preventDefault();
  dragOver.value = index;
}

function onDrop(index: number, event: DragEvent): void {
  if (dragFrom.value === null) return;
  event.preventDefault();
  move(dragFrom.value, index);
  resetDrag();
}

function resetDrag(): void {
  dragFrom.value = null;
  dragOver.value = null;
}

// ponytail: only SES for now (product call); the others are listed, greyed out.
const signatureTypeOptions = Object.values(SignatureType).map(value => ({
  label:
    value === SignatureType.ELECTRONIC_SIGNATURE
      ? signatureTypeMap[value]
      : `${signatureTypeMap[value]} · not available`,
  value,
  disabled: value !== SignatureType.ELECTRONIC_SIGNATURE,
}));

/** The prefilled expiry: the organisation's default number of days from today, within a year. */
function defaultExpiry(): number {
  return expiryDayInDays(expirationDays.value);
}

const name = ref<string>('');

// Each opening starts from what the wizard currently holds. Immediate, so a
// dialog that mounts already open is filled in too rather than left blank.
watch(
  () => props.show,
  show => {
    if (!show) return;
    name.value = props.fileName;
    provider.value = preselectedProvider();
    providerOverridden.value = false;
    signatureType.value = SignatureType.ELECTRONIC_SIGNATURE;
    orderedSigners.value = false;
    slotOrder.value = props.signers.map(line => line.slot);
    signingMessage.value = '';
    expiresAt.value = defaultExpiry();
  },
  { immediate: true }
);

/** What the backend would pick: the org's default, else the first available. */
function preselectedProvider(): SigningProvider | null {
  const first = providerOptions.value[0]?.value as SigningProvider | undefined;
  return defaultProvider.value ?? first ?? null;
}

function pickProvider(value: SigningProvider): void {
  provider.value = value;
  providerOverridden.value = true;
}

const folderLabel = computed<string>(() => props.folderLabel ?? 'My Vault');

const canSubmit = computed<boolean>(
  () =>
    name.value.trim().length > 0 &&
    !props.saving &&
    Boolean(provider.value) &&
    expiresAt.value !== null &&
    props.signerGaps?.length === 0
);

function submit(): void {
  if (!canSubmit.value) return;
  emit('save', {
    fileName: name.value.trim(),
    signing: {
      ...(providerOverridden.value && provider.value ? { provider: provider.value } : {}),
      signature_type: signatureType.value,
      ordered_signers: orderedSigners.value,
      ...(orderedSigners.value ? { signer_order: order.value.map(line => line.slot) } : {}),
      message: signingMessage.value.trim() || null,
      expiration_date: expiresAt.value === null ? null : expiryDayToIso(expiresAt.value),
    },
  });
}

function close(): void {
  if (props.saving) return;
  emit('update:show', false);
}

/**
 * Inline custom properties rather than `v-bind()`: the modal teleports to
 * <body>, and the vars `v-bind()` writes onto this component's root never
 * reach that subtree.
 */
const styleVars = {
  '--savedoc-border': gaiaNeutral.borderCard,
  '--savedoc-surface': gaiaNeutral.surfaceSoft,
  '--savedoc-divider': gaiaNeutral.borderDivider,
  '--savedoc-drop': gaiaNeutral.borderHover,
  '--savedoc-step-ring': gaiaNeutral.border,
  '--savedoc-step-surface': gaiaNeutral.surface,
  '--savedoc-step-ink': gaiaNeutral.ink,
};
</script>

<template>
  <gaia-modal
    :show="props.show"
    width="520px"
    :style="styleVars"
    :mask-closable="false"
    :closable="!props.saving"
    :close-on-esc="!props.saving"
    @update:show="(value: boolean) => emit('update:show', value)"
  >
    <template #header>
      <gaia-text size="subtitle" weight="semibold" tone="ink">
        Save &amp; send for signing
      </gaia-text>
    </template>

    <!-- The document as chosen on the first step: name and folder are shown,
         not changed — both change from the vault. -->
    <n-flex vertical :size="16">
      <gaia-text size="dense" tone="subtle" :style="{ lineHeight: 1.5 }">
        Saves the document as a contract in its folder, then sends it to the signers.
      </gaia-text>
      <gaia-form-field label="Document">
        <!-- A read-only row: CSS only for the row's surface and hairline,
             which no Naive prop provides. -->
        <n-flex align="center" :size="10" :wrap="false" class="row-surface">
          <n-icon :component="IconDocumentText" :size="18" :style="{ flexShrink: 0 }" />
          <n-flex vertical :size="0" :style="{ minWidth: 0 }">
            <gaia-text truncate size="dense" weight="semibold" tone="ink">{{ name }}</gaia-text>
            <n-flex align="center" :size="4" :wrap="false" :style="{ minWidth: 0 }">
              <n-icon :component="IconFolder" :size="12" :style="{ flexShrink: 0 }" />
              <gaia-text truncate size="meta" tone="subtle">{{ folderLabel }}</gaia-text>
            </n-flex>
          </n-flex>
        </n-flex>
      </gaia-form-field>
      <n-flex vertical :size="10">
        <!-- One provider leaves nothing to choose; several make it an override. -->
        <gaia-form-field v-if="providerOptions.length > 1" label="Provider">
          <gaia-select
            :value="provider"
            :options="providerOptions"
            :disabled="props.saving"
            style="width: 100%"
            @update:value="pickProvider"
          />
        </gaia-form-field>
        <gaia-form-field label="Signature type">
          <gaia-select
            v-model:value="signatureType"
            :options="signatureTypeOptions"
            :disabled="props.saving"
            style="width: 100%"
          />
        </gaia-form-field>
        <!-- Who signs, laid out as in the signing wizard's recipients step: a
             header with the at-once / in-order switch, then one bordered list;
             in order, numbered steps on a rail that drag (or arrow) to reorder. -->
        <n-flex v-if="order.length" vertical :size="8" :style="{ marginTop: '6px' }">
          <n-flex align="flex-end" justify="space-between" :size="12" :wrap="false">
            <n-flex vertical :size="2" :wrap="false" :style="{ minWidth: 0 }">
              <n-flex align="baseline" :size="8" :wrap="false">
                <gaia-text size="dense" weight="semibold" tone="ink">Signers</gaia-text>
                <gaia-text size="meta" tone="faint">{{ order.length }}</gaia-text>
              </n-flex>
              <gaia-text size="meta" tone="subtle">
                {{
                  orderedSigners
                    ? 'Each gets the document once the one before has signed.'
                    : 'Everyone gets the document at the same time.'
                }}
              </gaia-text>
            </n-flex>
            <gaia-segmented
              v-if="order.length > 1"
              :value="orderedSigners ? 'sequential' : 'parallel'"
              :options="ORDER_OPTIONS"
              :disabled="props.saving"
              @update:value="(mode: string) => (orderedSigners = mode === 'sequential')"
            />
          </n-flex>
          <div class="signer-list" :class="{ ordered: orderedSigners }">
            <div
              v-for="(signer, index) in order"
              :key="signer.slot"
              class="signer-row"
              :class="{ 'drag-target': dragOver === index && dragFrom !== index }"
              :draggable="orderedSigners && !props.saving"
              @dragstart="(event: DragEvent) => onDragStart(index, event)"
              @dragover="(event: DragEvent) => onDragOver(index, event)"
              @drop="(event: DragEvent) => onDrop(index, event)"
              @dragend="resetDrag"
            >
              <div class="lead">
                <span v-if="orderedSigners" class="step">{{ index + 1 }}</span>
                <gaia-avatar v-else :size="28">
                  {{ initialsOf(splitSigner(signer.label).person) }}
                </gaia-avatar>
                <span v-if="orderedSigners && index < order.length - 1" class="rail" />
              </div>
              <n-flex vertical :size="0" :wrap="false" :style="{ flex: 1, minWidth: 0 }">
                <gaia-text size="dense" weight="semibold" tone="ink" truncate>
                  {{ splitSigner(signer.label).person }}
                </gaia-text>
                <gaia-text
                  v-if="splitSigner(signer.label).company"
                  size="meta"
                  tone="subtle"
                  truncate
                >
                  Signs for {{ splitSigner(signer.label).company }}
                </gaia-text>
              </n-flex>
              <!-- The arrows keep it reorderable from the keyboard. -->
              <n-flex
                v-if="orderedSigners && order.length > 1"
                align="center"
                :size="2"
                :wrap="false"
                :style="{ flex: 'none' }"
              >
                <gaia-icon-button
                  :dimension="26"
                  quaternary
                  title="Sign earlier"
                  :disabled="props.saving || index === 0"
                  @click="move(index, index - 1)"
                >
                  <n-icon :component="IconArrowUp" :size="14" />
                </gaia-icon-button>
                <gaia-icon-button
                  :dimension="26"
                  quaternary
                  title="Sign later"
                  :disabled="props.saving || index === order.length - 1"
                  @click="move(index, index + 1)"
                >
                  <n-icon :component="IconArrowDown" :size="14" />
                </gaia-icon-button>
                <n-icon :component="IconReorder" :size="16" class="grip" title="Drag to reorder" />
              </n-flex>
            </div>
          </div>
        </n-flex>
        <gaia-form-field
          label="Expires"
          required
          :error="expiresAt === null ? 'Pick when the request expires' : undefined"
        >
          <gaia-date-picker
            v-model:value="expiresAt"
            type="date"
            :is-date-disabled="isUnavailableExpiryDay"
            :disabled="props.saving"
            style="width: 100%"
          />
        </gaia-form-field>
        <gaia-form-field label="Message to signers" hint="Optional · shown in the signing email">
          <!-- One line: the backend refuses line breaks in this message. -->
          <gaia-input
            v-model:value="signingMessage"
            :maxlength="10000"
            :disabled="props.saving"
            placeholder="Please sign before month end."
            style="width: 100%"
          />
        </gaia-form-field>
        <gaia-text
          v-for="gap in props.signerGaps"
          :key="gap"
          size="dense"
          tone="alert"
          :style="{ lineHeight: 1.5 }"
        >
          {{ gap }}
        </gaia-text>
      </n-flex>
    </n-flex>

    <template #footer>
      <n-flex justify="end" :size="8">
        <gaia-button :disabled="props.saving" @click="close">Cancel</gaia-button>
        <gaia-button type="primary" :loading="props.saving" :disabled="!canSubmit" @click="submit">
          Save &amp; sign
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
.row-surface {
  padding: 6px 8px 6px 12px;
  border: 1px solid var(--savedoc-border);
  border-radius: 8px;
  background: var(--savedoc-surface);
}

/* The signer list mirrors GaiaSigningRecipientsStep's: one bordered surface
   with hairlines, and in order a numbered rail plus drag cues. Naive has no
   list-group, step-rail or drag-and-drop primitive. */
.signer-list {
  border: 1px solid var(--savedoc-border);
  border-radius: 10px;
  overflow: hidden;
}

.signer-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 10px 10px 12px;
}

.signer-row + .signer-row {
  border-top: 1px solid var(--savedoc-divider);
}

.signer-row:hover {
  background: var(--savedoc-surface);
}

.signer-row.drag-target {
  outline: 2px dashed var(--savedoc-drop);
  outline-offset: -4px;
}

.signer-list.ordered .signer-row {
  cursor: grab;
}

/* In order the rail ties the rows together; hairlines would cut it. */
.signer-list.ordered .signer-row + .signer-row {
  border-top: 0;
}

/* Stretches the row so the rail can run down into the next row's step. */
.lead {
  align-self: stretch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 28px;
  flex: none;
  position: relative;
}

.step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: 1px solid var(--savedoc-step-ring);
  border-radius: 50%;
  background: var(--savedoc-step-surface);
  color: var(--savedoc-step-ink);
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  position: relative;
  z-index: 2;
}

/* From this step's circle down to the next one's: half a row below the
   centre, through the next row's top half. */
.rail {
  position: absolute;
  top: 50%;
  bottom: -50%;
  left: 50%;
  width: 1px;
  z-index: 1;
  background: var(--savedoc-step-ring);
}

.grip {
  margin-left: 4px;
  opacity: 0.45;
}

.signer-row:hover .grip {
  opacity: 0.8;
}
</style>
